import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';

import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ErrorStatusCodeEnum, ResponseStatusCodeEnum } from '@core/enums';
import { AuthService } from '@core/services/auth.service';
import { ErrorService } from '@core/services/error.service';
import { TokenService } from '@core/services/token.service';
import { getLanguage } from '@core/utils';
import { environment as env } from '@environments/environment';

import { TranslateService } from '@ngx-translate/core';

export class HttpErrorInterceptor implements HttpInterceptor {
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly errorService: ErrorService = inject(ErrorService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly router: Router = inject(Router);
  private readonly tokenService: TokenService = inject(TokenService);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError(err => {
        if (err.error instanceof Blob && err.error.type === 'application/json') {
          return this.blobErrorHandler(err.error);
        } else if (err.error && !err.error?.status) {
          this.errorHandler(err);
        } else if (!this.errorService.hasDialog) {
          this.showError(err);
        }
        return throwError(() => err.error);
      })
    );
  }

  private errorHandler(response: HttpErrorResponse): void {
    const errResponse = response.error;
    if (
      (response.url.includes(`${env.api.payment}/${env.api.sign}`) ||
        response.url.includes(env.signerPath) ||
        response.url.includes('excel') ||
        response.url.endsWith('files/add')) &&
      errResponse.errors &&
      Object.keys(errResponse.errors).length
    ) {
      return null;
    }

    switch (response?.status) {
      case ResponseStatusCodeEnum.BAD_REQUEST:
      case ResponseStatusCodeEnum.UNHANDLED_EXCEPTION:
        if (
          errResponse?.message &&
          !response?.url.includes('/authenticate') &&
          !response?.url.includes('/enter-2fa-code') &&
          !response?.url.includes('/forgot-password') &&
          !this.errorService.hasDialog &&
          !response?.url.includes('/set-password') &&
          !response?.url.includes('/merge') &&
          !response?.url.includes('/change-password') &&
          !response?.url.includes('/confirm-code') &&
          !response?.url.includes(`${env.api.feedbacks}/${env.api.create}`)
        ) {
          this.errorService.show$.next({ body: this.getErrorText(errResponse.errors) || errResponse.message });
        }
        break;
      case ResponseStatusCodeEnum.UNAUTHORIZED:
        if (errResponse.errorCode === ErrorStatusCodeEnum.ERROR_AUTH) {
          this.logout();
          break;
        }
        if (errResponse.errorCode === ErrorStatusCodeEnum.TOKEN_REFRESH_EXPIRED) {
          if (this.router.url.includes(env.api.identities)) {
            this.logout();
          }
          break;
        }
        return;
      case ErrorStatusCodeEnum.SERVICE_UNAVAILABLE:
        if (!this.errorService.hasTechnicalDialog) {
          this.errorService.serverError$.next({
            body: errResponse?.message || this.translate.instant('messages.unknown-server-error'),
          });
        }
        break;
      default:
        if (!this.errorService.hasDialog && !this.errorService.show$.closed) {
          this.errorService.show$.next({
            body: errResponse?.message || this.translate.instant('messages.unknown-server-error'),
          });
        }
    }
  }

  private logout(): void {
    this.authService.temporaryToken = null;
    this.tokenService.clearTokens();
    this.router.navigateByUrl(`/${getLanguage()}/auth`).catch();
  }

  private showError(err: HttpErrorResponse): void {
    if (err.url && !err.url.endsWith('ping') && !err.url.includes('version.json')) {
      let errorMessage = this.translate.instant('messages.unknown-server-error');

      if (!navigator.onLine) {
        errorMessage = this.translate.instant('messages.no-internet-connection');
      } else if (err.url.includes(env.signerPath)) {
        errorMessage = this.translate.instant('messages.signer-no-available');
        this.authService.temporaryToken = this.authService.oldTemporaryToken;
      } else if (err.error?.errorCode === ErrorStatusCodeEnum.SERVICE_UNAVAILABLE) {
        this.errorService.serverError$.next({
          body: err.error?.message || this.translate.instant('messages.unknown-server-error'),
        });
        return;
      }
      if (err && !this.errorService.hasDialog && !this.errorService.show$.closed) {
        this.errorService.show$.next({
          title: this.translate.instant('messages.error'),
          body: errorMessage,
        });
      }
    }
  }

  private blobErrorHandler(error: Blob): Observable<HttpEvent<any>> {
    return new Observable<HttpEvent<any>>(observer => {
      const reader = new FileReader();
      reader.onload = (): void => {
        try {
          const json = JSON.parse(reader.result as string);
          if (json.errorCode === ErrorStatusCodeEnum.BAD_REQUEST) {
            this.errorService.show$.next({ body: json.message });
          }
        } catch {
          console.log("Can't parse blob file");
        }
        observer.error(error);
      };
      reader.onerror = (): void => {
        observer.error(error);
      };
      reader.readAsText(error);
    });
  }

  private getErrorText(obj: any): string {
    let errorText: string;
    for (const key in obj) {
      errorText = obj[key];
    }

    return errorText;
  }
}
